import React, { useState, useEffect } from "react";
import { 
  Tabs, Form, Input, Button, Select, message, Table, Tag, Modal, 
  Card, Row, Col, Statistic, Empty, Badge, Popconfirm, Space, Tooltip
} from "antd";
import { 
  BugOutlined, MessageOutlined, DeleteOutlined, EditOutlined,
  ExclamationCircleOutlined, CheckCircleOutlined, 
  ClockCircleOutlined, SyncOutlined, SearchOutlined, PlusOutlined, LikeOutlined
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../../const";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Option } = Select;

const ISSUE_CATEGORIES = [
  "Login / Authentication",
  "Dashboard / Navigation",
  "Job Posting",
  "Talent Search",
  "Profile Management",
  "Payments / Billing",
  "Timesheet",
  "Reports",
  "Performance",
  "Other"
];

const FEEDBACK_CATEGORIES = [
  { value: "feature_request", label: "Feature Request" },
  { value: "performance", label: "Performance Improvement" },
  { value: "ui_ux", label: "UI/UX Enhancement" },
  { value: "documentation", label: "Documentation" },
  { value: "integration", label: "Integration Suggestion" },
  { value: "general", label: "General Feedback" },
];

const PageHeader = styled.div`
  margin-bottom: 24px;
  h1 { font-size: 24px; font-weight: 600; color: #014c75; margin: 0 0 8px 0; }
  p { color: #666; margin: 0; font-size: 14px; }
`;

const PageWrapper = styled.div`
  min-height: 100vh;

  .stats-row {
    margin-bottom: 24px;
  }

  .stat-card {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

    .ant-statistic-title {
      color: #64748b;
      font-size: 13px;
    }

    .ant-statistic-content-value {
      color: #1e293b;
      font-weight: 600;
    }
  }

  .main-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    overflow: hidden;
  }

  .ant-tabs-nav {
    margin: 0;
    padding: 0 24px;
    background: #fafafa;
    border-bottom: 1px solid #e8e8e8;
  }

  .tab-content {
    padding: 24px;
  }

  .filters-row {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;

    .ant-input-search {
      max-width: 300px;
    }

    .ant-select {
      min-width: 150px;
    }
  }

  .action-btn {
    background: linear-gradient(135deg, #01d9a9 0%, #00b894 100%);
    border: none;
    height: 40px;
    padding: 0 24px;
    border-radius: 8px;
    font-weight: 600;
    color: white;
  }

  .action-btn:hover {
    background: linear-gradient(135deg, #00b894 0%, #01d9a9 100%);
    color: white;
  }

  .feedback-btn {
    background: linear-gradient(135deg, #014c75 0%, #0369a1 100%);
    border: none;
    height: 40px;
    padding: 0 24px;
    border-radius: 8px;
    font-weight: 600;
    color: white;
  }

  .feedback-btn:hover {
    background: linear-gradient(135deg, #0369a1 0%, #014c75 100%);
    color: white;
  }
`;

const AdminIssuesFeedback = () => {
  const [activeTab, setActiveTab] = useState("issues");
  const [loading, setLoading] = useState(false);
  const [issues, setIssues] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState({ issues: { total: 0, open: 0, resolved: 0 }, feedback: { total: 0, acknowledged: 0, pending: 0 } });
  const [editModal, setEditModal] = useState({ visible: false, record: null });
  const [detailModal, setDetailModal] = useState({ visible: false, record: null });
  const [editForm] = Form.useForm();
  const [issueForm] = Form.useForm();
  const [feedbackForm] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [issueModalVisible, setIssueModalVisible] = useState(false);
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchRecords("issue");
    fetchRecords("feedback");
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_ORGANIZATIONS,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.organizations) {
        setOrganizations(response.data.organizations || []);
      }
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  const fetchUsersByOrganization = async (orgName) => {
    if (!orgName) {
      setOrgUsers([]);
      return;
    }
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_USER_MANAGEMENT,
        { organization: orgName, pageSize: 100 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.Response) {
        setOrgUsers(response.data.Response || []);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setOrgUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleOrgChange = (orgName, form) => {
    setSelectedOrg(orgName);
    form.setFieldValue("selectedUser", undefined);
    fetchUsersByOrganization(orgName);
  };

  const handleUserSelect = (userId, form) => {
    const user = orgUsers.find(u => u.id === userId);
    if (user) {
      form.setFieldValue("onBehalfOf", `${user.name} (${user.email})`);
    }
  };

  const resetModalState = () => {
    setSelectedOrg(null);
    setOrgUsers([]);
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_ISSUE_FEEDBACK_STATS,
        { showAll: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchRecords = async (type) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const params = { type, limit: 100, showAll: true };
      if (searchText) params.search = searchText;
      if (statusFilter) params.status = statusFilter;
      
      const response = await axios.post(
        API_CONST.GET_ISSUE_FEEDBACK_LIST,
        params,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        if (type === "issue") {
          setIssues(response.data.records);
        } else {
          setFeedback(response.data.records);
        }
      }
    } catch (error) {
      console.error("Error fetching records:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchRecords(activeTab === "issues" ? "issue" : "feedback");
  };

  const handleEdit = (record) => {
    setEditModal({ visible: true, record });
    editForm.setFieldsValue({
      status: record.status,
      priority: record.priority,
      resolution: record.resolution,
      adminNotes: record.adminNotes,
    });
  };

  const handleSave = async () => {
    try {
      const values = await editForm.validateFields();
      setSaving(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.put(
        `${API_CONST.UPDATE_ISSUE_FEEDBACK}/${editModal.record.id}`,
        values,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        message.success("Record updated successfully");
        setEditModal({ visible: false, record: null });
        fetchStats();
        fetchRecords(editModal.record.type);
      }
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      const token = localStorage.getItem("authToken");
      await axios.delete(
        `${API_CONST.DELETE_ISSUE_FEEDBACK}/${record.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Record deleted successfully");
      fetchStats();
      fetchRecords(record.type);
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to delete");
    }
  };

  const handleAcknowledge = async (record) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        `${API_CONST.ACKNOWLEDGE_ISSUE_FEEDBACK.replace(':id', record.id)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        message.success("Acknowledged successfully!");
        setDetailModal({ visible: false, record: null });
        fetchStats();
        fetchRecords(record.type);
      }
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to acknowledge");
    }
  };

  const handleSubmitIssue = async (values) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.CREATE_ISSUE_FEEDBACK,
        { ...values, type: "issue" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        message.success("Issue submitted successfully!");
        issueForm.resetFields();
        setIssueModalVisible(false);
        fetchStats();
        fetchRecords("issue");
      }
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to submit issue");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitFeedback = async (values) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.CREATE_ISSUE_FEEDBACK,
        { ...values, type: "feedback" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        message.success("Feedback submitted successfully!");
        feedbackForm.resetFields();
        setFeedbackModalVisible(false);
        fetchStats();
        fetchRecords("feedback");
      }
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusTag = (status) => {
    const config = {
      open: { color: "orange", icon: <ClockCircleOutlined /> },
      in_progress: { color: "blue", icon: <SyncOutlined spin /> },
      resolved: { color: "green", icon: <CheckCircleOutlined /> },
      closed: { color: "default", icon: <CheckCircleOutlined /> },
      acknowledged: { color: "cyan", icon: <LikeOutlined /> },
    };
    const { color, icon } = config[status] || { color: "default" };
    return <Tag color={color} icon={icon}>{status?.replace("_", " ").toUpperCase()}</Tag>;
  };

  const getPriorityTag = (priority) => {
    const colors = {
      low: "default",
      medium: "blue",
      high: "orange",
      critical: "red",
    };
    return <Tag color={colors[priority] || "default"}>{priority?.toUpperCase()}</Tag>;
  };

  const getFeedbackCategoryLabel = (value) => {
    const cat = FEEDBACK_CATEGORIES.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  const issueColumns = [
    { 
      title: "ID", 
      dataIndex: "id", 
      key: "id",
      width: 140,
      render: (id) => <span style={{ fontFamily: "monospace", fontSize: 12 }}>{id}</span>
    },
    { title: "Title", dataIndex: "title", key: "title", ellipsis: true },
    { 
      title: "Submitted By", 
      dataIndex: "submittedByName", 
      key: "submittedByName",
      width: 150,
      render: (name, record) => (
        <div>
          <div>{name}</div>
          <div style={{ fontSize: 11, color: "#64748b" }}>{record.organizationName}</div>
          {record.submittedOnBehalfOf && (
            <div style={{ fontSize: 10, color: "#f59e0b" }}>On behalf of: {record.submittedOnBehalfOf}</div>
          )}
        </div>
      )
    },
    { title: "Category", dataIndex: "category", key: "category", width: 130 },
    { 
      title: "Priority", 
      dataIndex: "priority", 
      key: "priority",
      width: 90,
      render: (p) => getPriorityTag(p)
    },
    { 
      title: "Status", 
      dataIndex: "status", 
      key: "status",
      width: 120,
      render: (s) => getStatusTag(s)
    },
    { 
      title: "Created", 
      dataIndex: "createdAt", 
      key: "createdAt",
      width: 100,
      render: (d) => dayjs(d).format("MMM DD, YYYY")
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => setDetailModal({ visible: true, record })}>
            View
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          {(record.status === "resolved" || record.status === "closed") && record.status !== "acknowledged" && (
            <Tooltip title="Acknowledge">
              <Button 
                type="link" 
                size="small" 
                icon={<LikeOutlined />} 
                style={{ color: "#01d9a9" }}
                onClick={() => handleAcknowledge(record)} 
              />
            </Tooltip>
          )}
          <Popconfirm
            title="Are you sure you want to delete this?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const feedbackColumns = [
    { 
      title: "ID", 
      dataIndex: "id", 
      key: "id",
      width: 140,
      render: (id) => <span style={{ fontFamily: "monospace", fontSize: 12 }}>{id}</span>
    },
    { title: "Title", dataIndex: "title", key: "title", ellipsis: true },
    { 
      title: "Submitted By", 
      dataIndex: "submittedByName", 
      key: "submittedByName",
      width: 150,
      render: (name, record) => (
        <div>
          <div>{name}</div>
          <div style={{ fontSize: 11, color: "#64748b" }}>{record.organizationName}</div>
          {record.submittedOnBehalfOf && (
            <div style={{ fontSize: 10, color: "#f59e0b" }}>On behalf of: {record.submittedOnBehalfOf}</div>
          )}
        </div>
      )
    },
    { 
      title: "Type", 
      dataIndex: "feedbackCategory", 
      key: "feedbackCategory",
      width: 160,
      render: (c) => <Tag color="purple">{getFeedbackCategoryLabel(c)}</Tag>
    },
    { 
      title: "Status", 
      dataIndex: "status", 
      key: "status",
      width: 130,
      render: (s) => getStatusTag(s)
    },
    { 
      title: "Created", 
      dataIndex: "createdAt", 
      key: "createdAt",
      width: 100,
      render: (d) => dayjs(d).format("MMM DD, YYYY")
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => setDetailModal({ visible: true, record })}>
            View
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title="Are you sure you want to delete this?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: "issues",
      label: (
        <span>
          <BugOutlined /> Issues
          {stats.issues.open > 0 && <Badge count={stats.issues.open} style={{ marginLeft: 8 }} />}
        </span>
      ),
      children: (
        <div className="tab-content">
          <div className="filters-row">
            <Input.Search 
              placeholder="Search by ID, title, or description" 
              allowClear
              onSearch={handleSearch}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
            />
            <Select
              placeholder="Filter by status"
              allowClear
              value={statusFilter || undefined}
              onChange={(val) => { setStatusFilter(val || ""); }}
              style={{ width: 150 }}
            >
              <Option value="open">Open</Option>
              <Option value="in_progress">In Progress</Option>
              <Option value="resolved">Resolved</Option>
              <Option value="closed">Closed</Option>
              <Option value="acknowledged">Acknowledged</Option>
            </Select>
            <Button onClick={handleSearch}>Apply</Button>
          </div>
          <Table 
            columns={issueColumns} 
            dataSource={issues} 
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
            size="middle"
          />
        </div>
      ),
    },
    {
      key: "feedback",
      label: (
        <span>
          <MessageOutlined /> Feedback
          {stats.feedback.pending > 0 && <Badge count={stats.feedback.pending} style={{ marginLeft: 8, backgroundColor: "#014c75" }} />}
        </span>
      ),
      children: (
        <div className="tab-content">
          <div className="filters-row">
            <Input.Search 
              placeholder="Search by ID, title, or description" 
              allowClear
              onSearch={handleSearch}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
            />
            <Select
              placeholder="Filter by status"
              allowClear
              value={statusFilter || undefined}
              onChange={(val) => { setStatusFilter(val || ""); }}
              style={{ width: 150 }}
            >
              <Option value="open">Pending</Option>
              <Option value="acknowledged">Acknowledged</Option>
            </Select>
            <Button onClick={handleSearch}>Apply</Button>
          </div>
          <Table 
            columns={feedbackColumns} 
            dataSource={feedback} 
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
            size="middle"
          />
        </div>
      ),
    },
  ];

  return (
    <PageWrapper>
      <PageHeader><h1>Issues & Feedback</h1><p>Manage and respond to user-submitted issues and feedback</p></PageHeader>

      <Row gutter={16} className="stats-row">
        <Col span={4}>
          <Card className="stat-card">
            <Statistic 
              title="Total Issues" 
              value={stats.issues.total} 
              prefix={<BugOutlined style={{ color: "#f59e0b" }} />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card className="stat-card">
            <Statistic 
              title="Open Issues" 
              value={stats.issues.open} 
              prefix={<ClockCircleOutlined style={{ color: "#3b82f6" }} />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card className="stat-card">
            <Statistic 
              title="Resolved Issues" 
              value={stats.issues.resolved} 
              prefix={<CheckCircleOutlined style={{ color: "#10b981" }} />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card className="stat-card">
            <Statistic 
              title="Total Feedback" 
              value={stats.feedback.total} 
              prefix={<MessageOutlined style={{ color: "#014c75" }} />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card className="stat-card">
            <Statistic 
              title="Pending Feedback" 
              value={stats.feedback.pending} 
              prefix={<ClockCircleOutlined style={{ color: "#f59e0b" }} />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card className="stat-card">
            <Statistic 
              title="Acknowledged" 
              value={stats.feedback.acknowledged} 
              prefix={<LikeOutlined style={{ color: "#10b981" }} />}
            />
          </Card>
        </Col>
      </Row>

      <Card className="main-card">
        <Tabs 
          activeKey={activeTab} 
          onChange={(key) => { setActiveTab(key); setStatusFilter(""); setSearchText(""); }}
          items={tabItems}
          tabBarExtraContent={
            <div style={{ display: 'flex', gap: 12, paddingRight: 8 }}>
              <Button 
                type="primary" 
                icon={<BugOutlined />} 
                onClick={() => setIssueModalVisible(true)}
              >
                Report Issue
              </Button>
              <Button 
                type="primary" 
                icon={<MessageOutlined />} 
                onClick={() => setFeedbackModalVisible(true)}
              >
                Add Feedback
              </Button>
            </div>
          }
        />
      </Card>

      <Modal
        title={<><BugOutlined style={{ marginRight: 8 }} /> Report Issue on Behalf of User</>}
        open={issueModalVisible}
        onCancel={() => { setIssueModalVisible(false); issueForm.resetFields(); resetModalState(); }}
        footer={null}
        width={600}
      >
        <p style={{ color: "#64748b", marginBottom: 24 }}>
          Report an issue on behalf of a customer or partner.
        </p>
        <Form form={issueForm} layout="vertical" onFinish={handleSubmitIssue}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="selectedOrg"
                label="Organization"
              >
                <Select 
                  placeholder="Select organization" 
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  onChange={(val) => handleOrgChange(val, issueForm)}
                >
                  {organizations.map((org) => (
                    <Option key={org.id} value={org.name}>
                      {org.name} {org.type && <span style={{ color: "#94a3b8" }}>({org.type})</span>}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="selectedUser"
                label="User"
              >
                <Select 
                  placeholder={selectedOrg ? "Select user" : "Select organization first"}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  loading={loadingUsers}
                  disabled={!selectedOrg}
                  onChange={(val) => handleUserSelect(val, issueForm)}
                >
                  {orgUsers.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="onBehalfOf" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="category"
            label="Issue Category"
            rules={[{ required: true, message: "Please select a category" }]}
          >
            <Select placeholder="Select the area where the issue occurred">
              {ISSUE_CATEGORIES.map((cat) => (
                <Option key={cat} value={cat}>{cat}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="title"
            label="Issue Title"
            rules={[{ required: true, message: "Please enter a title" }]}
          >
            <Input placeholder="Brief description of the issue" maxLength={100} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please describe the issue" }]}
          >
            <TextArea 
              rows={4} 
              placeholder="Provide details about the issue"
              maxLength={2000}
              showCount
            />
          </Form.Item>
          <Form.Item
            name="priority"
            label="Priority"
            initialValue="medium"
          >
            <Select>
              <Option value="low">Low</Option>
              <Option value="medium">Medium</Option>
              <Option value="high">High</Option>
              <Option value="critical">Critical</Option>
            </Select>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => { setIssueModalVisible(false); issueForm.resetFields(); }}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={submitting}
                style={{ background: "linear-gradient(135deg, #01d9a9 0%, #00b894 100%)", border: "none" }}
              >
                Submit Issue
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={<><MessageOutlined style={{ marginRight: 8 }} /> Add Feedback on Behalf of User</>}
        open={feedbackModalVisible}
        onCancel={() => { setFeedbackModalVisible(false); feedbackForm.resetFields(); resetModalState(); }}
        footer={null}
        width={600}
      >
        <p style={{ color: "#64748b", marginBottom: 24 }}>
          Record feedback from a customer or partner.
        </p>
        <Form form={feedbackForm} layout="vertical" onFinish={handleSubmitFeedback}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="selectedOrg"
                label="Organization"
              >
                <Select 
                  placeholder="Select organization" 
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  onChange={(val) => handleOrgChange(val, feedbackForm)}
                >
                  {organizations.map((org) => (
                    <Option key={org.id} value={org.name}>
                      {org.name} {org.type && <span style={{ color: "#94a3b8" }}>({org.type})</span>}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="selectedUser"
                label="User"
              >
                <Select 
                  placeholder={selectedOrg ? "Select user" : "Select organization first"}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  loading={loadingUsers}
                  disabled={!selectedOrg}
                  onChange={(val) => handleUserSelect(val, feedbackForm)}
                >
                  {orgUsers.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="onBehalfOf" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="feedbackCategory"
            label="Feedback Type"
            rules={[{ required: true, message: "Please select a feedback type" }]}
          >
            <Select placeholder="What kind of feedback is this?">
              {FEEDBACK_CATEGORIES.map((cat) => (
                <Option key={cat.value} value={cat.value}>{cat.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Please enter a title" }]}
          >
            <Input placeholder="Brief summary of the feedback" maxLength={100} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please describe the feedback" }]}
          >
            <TextArea 
              rows={4} 
              placeholder="Detailed feedback description"
              maxLength={2000}
              showCount
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => { setFeedbackModalVisible(false); feedbackForm.resetFields(); }}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={submitting}
                style={{ background: "linear-gradient(135deg, #014c75 0%, #0369a1 100%)", border: "none" }}
              >
                Submit Feedback
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editModal.record?.type === "issue" ? "Update Issue" : "Update Feedback"}
        open={editModal.visible}
        onCancel={() => setEditModal({ visible: false, record: null })}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Save Changes"
        okButtonProps={{ style: { background: "#01d9a9", borderColor: "#01d9a9" } }}
        width={600}
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="status" label="Status">
            <Select>
              <Option value="open">Open</Option>
              <Option value="in_progress">In Progress</Option>
              <Option value="resolved">Resolved</Option>
              <Option value="closed">Closed</Option>
              <Option value="acknowledged">Acknowledged</Option>
            </Select>
          </Form.Item>
          {editModal.record?.type === "issue" && (
            <Form.Item name="priority" label="Priority">
              <Select>
                <Option value="low">Low</Option>
                <Option value="medium">Medium</Option>
                <Option value="high">High</Option>
                <Option value="critical">Critical</Option>
              </Select>
            </Form.Item>
          )}
          <Form.Item name="resolution" label="Resolution / Response">
            <TextArea rows={3} placeholder="Provide resolution details or response" />
          </Form.Item>
          <Form.Item name="adminNotes" label="Admin Notes (Internal)">
            <TextArea rows={2} placeholder="Internal notes (not visible to user)" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={detailModal.record?.type === "issue" ? "Issue Details" : "Feedback Details"}
        open={detailModal.visible}
        onCancel={() => setDetailModal({ visible: false, record: null })}
        footer={
          detailModal.record?.type === "issue" && 
          (detailModal.record?.status === "resolved" || detailModal.record?.status === "closed") && 
          detailModal.record?.status !== "acknowledged" ? (
            <Space>
              <Button onClick={() => setDetailModal({ visible: false, record: null })}>Close</Button>
              <Button 
                type="primary" 
                icon={<LikeOutlined />}
                onClick={() => handleAcknowledge(detailModal.record)}
                style={{ background: "#01d9a9", border: "none" }}
              >
                Acknowledge
              </Button>
            </Space>
          ) : (
            <Button onClick={() => setDetailModal({ visible: false, record: null })}>Close</Button>
          )
        }
        width={700}
      >
        {detailModal.record && (
          <div style={{ lineHeight: 2 }}>
            <Row gutter={24}>
              <Col span={12}>
                <p><strong>ID:</strong> <code>{detailModal.record.id}</code></p>
                <p><strong>Title:</strong> {detailModal.record.title}</p>
                {detailModal.record.type === "issue" ? (
                  <>
                    <p><strong>Category:</strong> {detailModal.record.category}</p>
                    <p><strong>Priority:</strong> {getPriorityTag(detailModal.record.priority)}</p>
                  </>
                ) : (
                  <p><strong>Type:</strong> <Tag color="purple">{getFeedbackCategoryLabel(detailModal.record.feedbackCategory)}</Tag></p>
                )}
                <p><strong>Status:</strong> {getStatusTag(detailModal.record.status)}</p>
              </Col>
              <Col span={12}>
                <p><strong>Submitted By:</strong> {detailModal.record.submittedByName}</p>
                <p><strong>Email:</strong> {detailModal.record.submittedByEmail}</p>
                <p><strong>Organization:</strong> {detailModal.record.organizationName || "N/A"}</p>
                {detailModal.record.submittedOnBehalfOf && (
                  <p><strong>On Behalf Of:</strong> <Tag color="orange">{detailModal.record.submittedOnBehalfOf}</Tag></p>
                )}
                <p><strong>Submitted:</strong> {dayjs(detailModal.record.createdAt).format("MMMM DD, YYYY [at] HH:mm")}</p>
              </Col>
            </Row>
            <div style={{ marginTop: 16, padding: 16, background: "#f5f7fa", borderRadius: 8 }}>
              <strong>Description:</strong>
              <p style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>{detailModal.record.description}</p>
            </div>
            {detailModal.record.resolution && (
              <div style={{ marginTop: 16, padding: 16, background: "#ecfdf5", borderRadius: 8 }}>
                <strong style={{ color: "#059669" }}>Resolution:</strong>
                <p style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>{detailModal.record.resolution}</p>
              </div>
            )}
            {detailModal.record.adminNotes && (
              <div style={{ marginTop: 16, padding: 16, background: "#fff7ed", borderRadius: 8 }}>
                <strong style={{ color: "#c2410c" }}>Admin Notes:</strong>
                <p style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>{detailModal.record.adminNotes}</p>
              </div>
            )}
            {detailModal.record.acknowledgedAt && (
              <p style={{ marginTop: 16, color: "#059669" }}>
                <CheckCircleOutlined style={{ marginRight: 8 }} />
                Acknowledged on {dayjs(detailModal.record.acknowledgedAt).format("MMMM DD, YYYY [at] HH:mm")}
                {detailModal.record.acknowledgedByName && ` by ${detailModal.record.acknowledgedByName}`}
              </p>
            )}
          </div>
        )}
      </Modal>
    </PageWrapper>
  );
};

export default AdminIssuesFeedback;
